import { useState } from 'react';
import { Box } from '@mui/material';
import { useLiveMatch } from '../LiveMatchContext.tsx';
import { TeamSide } from '../liveMatchTypes.ts';
import ScoreBoard from './ScoreBoard.tsx';
import VolleyballField, { FieldZoomButton, FieldZoomDialog } from './VolleyballField.tsx';
import ActionPanel from './ActionPanel.tsx';
import TimeoutOverlay from './TimeoutOverlay.tsx';
import SubstitutionDialog from './SubstitutionDialog.tsx';
import SetEndSummaryScreen from './SetEndSummaryScreen.tsx';
import MatchEndScreen from './MatchEndScreen.tsx';

// 2.32: 8 sostituzioni per set (circolare integrazione regolamento 01/11/2026)
const MAX_SUBSTITUTIONS = 8;

export default function LiveGameScreen({ onSaveSuccess }: { onSaveSuccess?: () => void }) {
  const { state } = useLiveMatch();

  const [swapped, setSwapped]         = useState(false);
  const [fieldZoom, setFieldZoom]     = useState(false);
  const [timeoutTeam, setTimeoutTeam] = useState<TeamSide | null>(null);
  const [subDialog, setSubDialog]     = useState<{ side: TeamSide; outPlayerId: number } | null>(null);

  const leftSide:  TeamSide = swapped ? 'AWAY' : 'HOME';
  const rightSide: TeamSide = swapped ? 'HOME' : 'AWAY';

  function getSubCount(side: TeamSide): number {
    return state.eventLog.filter(
      (e) =>
        e.type === (side === 'HOME' ? 'SUBSTITUTION_HOME' : 'SUBSTITUTION_AWAY') &&
        e.setNumber === state.currentSet,
    ).length;
  }

  function handlePlayerClick(playerId: number, side: TeamSide) {
    if (getSubCount(side) >= MAX_SUBSTITUTIONS) return;
    setSubDialog({ side, outPlayerId: playerId });
  }

  if (state.currentPhase === 'SET_END_SUMMARY') return <SetEndSummaryScreen />;
  if (state.currentPhase === 'MATCH_END')       return <MatchEndScreen onSaveSuccess={onSaveSuccess} />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        bgcolor: '#1a1a2e',
        position: 'relative',
      }}
    >
      <ScoreBoard swapped={swapped} />

      {/*
        Responsive layout:
        xs  → "field field / left right"  (campo in alto a piena larghezza, pannelli sotto in riga)
        sm+ → "left field right"           (3 colonne classiche)
      */}
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: '25% 50% 25%' },
          gridTemplateRows:    { xs: 'auto 1fr', sm: '1fr' },
          gridTemplateAreas: {
            xs: '"field field" "left right"',
            sm: '"left  field  right"',
          },
          overflow: 'hidden',
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        {/* Pannello sinistro */}
        <Box
          sx={{
            gridArea: 'left',
            borderRight: { sm: '1px solid rgba(255,255,255,0.08)' },
            borderTop:   { xs: '1px solid rgba(255,255,255,0.08)', sm: 'none' },
          }}
        >
          <ActionPanel
            side={leftSide}
            isGlobalSide={leftSide === 'HOME'}
            onTimeout={() => setTimeoutTeam(leftSide)}
            onSwap={() => setSwapped((s) => !s)}
            maxSubsReached={getSubCount(leftSide) >= MAX_SUBSTITUTIONS}
          />
        </Box>

        {/* Campo */}
        <Box
          sx={{
            gridArea: 'field',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <VolleyballField onPlayerClick={handlePlayerClick} swapped={swapped} />
          <Box sx={{ position: 'absolute', bottom: 6, right: 6 }}>
            <FieldZoomButton onClick={() => setFieldZoom(true)} />
          </Box>
        </Box>

        {/* Pannello destro */}
        <Box
          sx={{
            gridArea: 'right',
            borderLeft: { sm: '1px solid rgba(255,255,255,0.08)' },
            borderTop:  { xs: '1px solid rgba(255,255,255,0.08)', sm: 'none' },
          }}
        >
          <ActionPanel
            side={rightSide}
            isGlobalSide={rightSide === 'HOME'}
            onTimeout={() => setTimeoutTeam(rightSide)}
            onSwap={() => setSwapped((s) => !s)}
            maxSubsReached={getSubCount(rightSide) >= MAX_SUBSTITUTIONS}
          />
        </Box>
      </Box>

      <TimeoutOverlay
        team={timeoutTeam}
        teamName={timeoutTeam === 'HOME' ? state.homeTeamName : state.awayTeamName}
        onClose={() => setTimeoutTeam(null)}
      />

      {subDialog && (
        <SubstitutionDialog
          open
          side={subDialog.side}
          outPlayerId={subDialog.outPlayerId}
          onClose={() => setSubDialog(null)}
        />
      )}

      <FieldZoomDialog open={fieldZoom} onClose={() => setFieldZoom(false)} swapped={swapped} />
    </Box>
  );
}
